import classNames from "classnames";
import React from "react";

import {
    ErrorFieldValue,
    GenericErrorSet,
    IBooleanField,
    IChoiceField,
    IField,
    IFieldSet,
    IFuzzyDurationField,
    INumberField,
    IPhoneNumberField,
    IRadioChoiceField,
    IStringField,
    ITextBoxField,
    IValueSet,
    ValidationTypes,
} from "../../models/formFields";
import { strToBool } from "../../utils/format";
import { notEmpty } from "../../utils/functional";
import { assertNever } from "../../utils/never";
import { FormCheckbox } from "../FormCheckbox";
import { FormFuzzyDurationSelect } from "../FormFuzzyDurationSelect";
import { FormInput } from "../FormInput";
import { FormNumber } from "../FormNumber";
import { FormPhoneNumber } from "../FormPhoneNumber";
import { FormRadioSelect } from "../FormRadioSelect";
import { FormSelect } from "../FormSelect";
import { FormTextbox } from "../FormTextbox";

import styles from "./index.module.scss";

type InputElements = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/**
 * Helper: Create onChange handler for AutoFields
 */
const createOnChangeHandler =
    <T extends string>(
        propsOnChange: (name: T, value: string) => void,
    ): (<ElementType extends InputElements>(
        event: React.FormEvent<ElementType>,
    ) => void) =>
    <ElementType extends InputElements>(
        event: React.FormEvent<ElementType>,
    ): void => {
        const name = event.currentTarget.name as T;
        const target = event.currentTarget;

        // For checkboxes, use the checked property instead of value
        if (target.type === "checkbox" && "checked" in target) {
            propsOnChange(name, target.checked ? "true" : "false");
        } else {
            propsOnChange(name, target.value);
        }
    };

/**
 * Helper: Get basic field props shared by all field types
 */
const getBasicFieldProps = <T extends string>(
    name: T,
    errors: ErrorFieldValue,
    disabled: boolean | undefined,
    onChange: <ElementType extends InputElements>(
        event: React.FormEvent<ElementType>,
    ) => void,
    onValidStateChange: (fieldName: T, errorMessages: string[]) => void,
    showErrorMessages: boolean | undefined,
): {
    id: string;
    name: T;
    key: T;
    errors: string[];
    disabled: boolean | undefined;
    onChange: <ElementType extends InputElements>(
        event: React.FormEvent<ElementType>,
    ) => void;
    onValidStateChange: (fieldName: T, errorMessages: string[]) => void;
    showErrorMessages: boolean | undefined;
} => {
    return {
        id: `id_${name}`,
        name: name,
        key: name,
        errors: [errors].filter(notEmpty).flat(),
        disabled: disabled,
        onChange: onChange,
        onValidStateChange: onValidStateChange,
        showErrorMessages: showErrorMessages,
    };
};

/**
 * Helper: Build checkbox field component
 */
const buildCheckboxField = <T extends string>(
    name: T,
    field: IBooleanField<T>,
    value: boolean,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    return (
        <FormCheckbox
            className={styles.formCheckbox}
            wrapperCSSClass={styles.formCheckboxWrapper}
            {...field}
            {...basicProps}
            checked={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build choice/select field component
 */
const buildChoiceField = <T extends string>(
    name: T,
    field: IChoiceField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    // Normalize any falsy (undefined, null, 0, etc) given value to be an empty string
    value = value || "";

    const choices = [...(field.choices || [])];
    const values = choices.map((opt) => {
        return opt.value;
    });

    const prependOption = (optionLabel: string, optionValue: string) => {
        values.unshift(optionValue);
        choices.unshift({
            value: optionValue || "",
            label: optionLabel || "",
        });
    };

    // Make sure there is a blank option if the field is not required.
    if (!field.required && values.indexOf("") === -1) {
        prependOption("", "");
    }

    // If the current value isn't an option (maybe the current value is blank on initial load), add the value until the change it.
    if (values.indexOf(value) === -1) {
        prependOption("", value);
    }

    const labelClasses = classNames({
        [styles.formLabel]: field.required,
        [styles.optionalFormLabel]: !field.required,
    });

    return (
        <FormSelect
            className={styles.formSelect}
            labelCSSClass={labelClasses}
            wrapperCSSClass={styles.formSelectWrapper}
            {...field}
            {...basicProps}
            choices={choices}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build radio choice field component
 */
const buildRadioChoiceField = <T extends string>(
    name: T,
    field: IRadioChoiceField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    return (
        <FormRadioSelect
            {...field}
            {...basicProps}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build number field component
 */
const buildNumberField = <T extends string>(
    name: T,
    field: INumberField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    const labelClasses = classNames({
        [styles.formLabel]: field.required,
        [styles.optionalFormLabel]: !field.required,
    });
    return (
        <FormNumber
            className={styles.formField}
            labelCSSClass={labelClasses}
            wrapperCSSClass={styles.formWrapper}
            {...field}
            {...basicProps}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build textbox field component
 */
const buildTextBox = <T extends string>(
    name: T,
    field: ITextBoxField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    return (
        <FormTextbox
            {...field}
            {...basicProps}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build fuzzy duration field component
 */
const buildFuzzyDurationField = <T extends string>(
    name: T,
    field: IFuzzyDurationField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    return (
        <FormFuzzyDurationSelect
            {...field}
            {...basicProps}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build phone number field component
 */
const buildPhoneField = <T extends string>(
    name: T,
    field: IPhoneNumberField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    const labelClasses = classNames({
        [styles.formLabel]: field.required,
        [styles.optionalFormLabel]: !field.required,
    });
    return (
        <FormPhoneNumber
            className={styles.formField}
            labelCSSClass={labelClasses}
            wrapperCSSClass={styles.formWrapper}
            {...field}
            {...basicProps}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build string/text input field component
 */
const buildInput = <T extends string>(
    name: T,
    field: IStringField<T>,
    value: string,
    errors: ErrorFieldValue,
    basicProps: ReturnType<typeof getBasicFieldProps<T>>,
): React.ReactNode => {
    const labelClasses = classNames({
        [styles.formLabel]: field.required,
        [styles.optionalFormLabel]: !field.required,
    });
    return (
        <FormInput
            className={styles.formField}
            labelCSSClass={labelClasses}
            wrapperCSSClass={styles.formWrapper}
            {...field}
            {...basicProps}
            value={value}
            key={basicProps.key}
        />
    );
};

/**
 * Helper: Build field component based on config type
 */
const buildField = <T extends string>(
    name: T,
    fields: IFieldSet<T>,
    values: Partial<IValueSet<T>> | undefined,
    errors: GenericErrorSet<T> | undefined,
    disabled: boolean | undefined,
    onChange: <ElementType extends InputElements>(
        event: React.FormEvent<ElementType>,
    ) => void,
    onValidStateChange: (fieldName: T, errorMessages: string[]) => void,
    showErrorMessages: boolean | undefined,
): React.ReactNode => {
    const value = values ? values[name] : null;
    const fieldErrors = errors ? errors[name] : undefined;
    const baseConfig = fields[name];

    // Add required field validation immutably
    let config: IField<T>;
    if (baseConfig.required) {
        const baseValidation = baseConfig.validation;
        const validation = baseValidation
            ? [baseValidation].flat()
            : ([] as ValidationTypes[]);
        if (!validation.includes("required")) {
            config = {
                ...baseConfig,
                validation: [...validation, "required"],
            };
        } else {
            config = baseConfig;
        }
    } else {
        config = baseConfig;
    }

    const basicProps = getBasicFieldProps(
        name,
        fieldErrors,
        disabled,
        onChange,
        onValidStateChange,
        showErrorMessages,
    );

    switch (config.type) {
        case undefined:
        case null:
            return null;

        case "boolean":
        case "checkbox":
        case "radio":
            return buildCheckboxField(
                name,
                config,
                strToBool(value ? (value as string) : "false"),
                fieldErrors,
                basicProps,
            );

        case "choice":
            return buildChoiceField(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        case "radio-choice":
            return buildRadioChoiceField(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        case "integer":
        case "number":
            return buildNumberField(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        case "textbox":
            return buildTextBox(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        case "fuzzy-duration":
            return buildFuzzyDurationField(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        case "phone":
            return buildPhoneField(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        case "email":
        case "date":
        case "datetime":
        case "string":
        case "text":
        case "tel":
        case "password":
            return buildInput(
                name,
                config,
                value as string,
                fieldErrors,
                basicProps,
            );

        default:
            assertNever(config);
    }

    return null;
};

export const AutoFields = <T extends string>(props: {
    heading: string;
    fieldOrder: readonly T[];
    fields: IFieldSet<T>;
    disabled?: boolean;
    values?: Partial<IValueSet<T>>;
    errors?: GenericErrorSet<T>;
    showErrorMessages?: boolean;
    onChange: (name: T, value: string) => void;
    onValidStateChange: (fieldName: T, errorMessages: string[]) => void;
}): React.ReactNode => {
    const onChange = createOnChangeHandler(props.onChange);

    const fields = props.fieldOrder
        .filter((name) => {
            return !!props.fields[name];
        })
        .map((name) => {
            return buildField(
                name,
                props.fields,
                props.values,
                props.errors,
                props.disabled,
                onChange,
                props.onValidStateChange,
                props.showErrorMessages,
            );
        });

    if (!fields.length) {
        return null;
    }

    return (
        <>
            <h3 className={styles.heading}>{props.heading}</h3>
            {fields}
        </>
    );
};
